import { ControlledTextField } from '@if/mui'
import { Done as DoneIcon } from '@mui/icons-material'
import CloseIcon from '@mui/icons-material/Close'
import DeleteIcon from '@mui/icons-material/Delete'
import HistoryIcon from '@mui/icons-material/History'
import {
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material'
import AsyncActionButton from '@src/shared/components/AsyncActionButton'
import SelectComponent from '@src/shared/components/SelectComponent'
import { UpdateExceptionLogDto, useUpdateExceptionLog } from '@src/tools/error-tracker/api/exceptions-api'
import { reportErrorToConsole } from '@src/shared/services/error-logger'
import { EExceptionStatus } from '@src/tools/error-tracker/types/Constants'
import { ExceptionLog } from '@src/tools/error-tracker/types/ExceptionLog'
import { SelectValueListItem } from '@src/shared/types/SelectValueListItem'
import { memo, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

interface EditExceptionFormData {
  devopsTicketNumber: string
  devopsTicketNumbers: string[]
  status: string
  note: string
}

interface props {
  exceptionLog: ExceptionLog | null
  exceptionStatuses: SelectValueListItem[]
  open: boolean
  onSuccess: () => void
  onCancel: () => void
}

const EditExceptionDialog = ({ exceptionLog, exceptionStatuses, open, onSuccess, onCancel }: props) => {
  const { t } = useTranslation()
  const DevopsBaseUrl = 'https://dev.azure.com/IcebergFinance/IcebergFinance/_workitems/edit/'

  const [updateExceptionLog, isUpdating, updateError, resetUpdate] = useUpdateExceptionLog()

  const { register, control, getValues, handleSubmit, reset, watch, setValue } = useForm<EditExceptionFormData>({
    defaultValues: {
      devopsTicketNumber: exceptionLog?.devopsTicketNumber || '',
      devopsTicketNumbers: exceptionLog?.devopsTicketNumbers || [],
      status: exceptionLog?.status || EExceptionStatus.New,
      note: exceptionLog?.note || '',
    },
  })

  // Watch form values
  const devopsTicketNumbers = watch('devopsTicketNumbers')

  useEffect(() => {
    reset({
      devopsTicketNumber: exceptionLog?.devopsTicketNumber || '',
      devopsTicketNumbers: exceptionLog?.devopsTicketNumbers || [],
      status: exceptionLog?.status || EExceptionStatus.New,
      note: exceptionLog?.note || '',
    })
    resetUpdate()
  }, [exceptionLog, open, reset, resetUpdate])

  const handleFormSubmit = async (formData: EditExceptionFormData) => {
    if (exceptionLog?.id) {
      try {
        const dto: UpdateExceptionLogDto = {
          devopsTicketNumber: formData.devopsTicketNumber,
          devopsTicketNumbers: formData.devopsTicketNumbers,
          status: formData.status,
          note: formData.note,
        }
        await updateExceptionLog({ id: exceptionLog.id, dto })
        onSuccess()
      } catch (err) {
        // Error is handled by react-query and shown via updateError
        reportErrorToConsole(err)
      }
    }
  }

  // Handle ticket removal (client-side only)
  const handleRemoveTicket = (ticketNumber: string) => {
    const currentTickets = getValues('devopsTicketNumbers')
    const updatedTickets = currentTickets.filter((ticket) => ticket !== ticketNumber)

    // Update the ticket array
    setValue('devopsTicketNumbers', updatedTickets)

    // If removing current ticket, set to most recent or empty
    const currentTicket = getValues('devopsTicketNumber')
    if (currentTicket === ticketNumber) {
      const newCurrentTicket = updatedTickets.length > 0 ? updatedTickets[updatedTickets.length - 1] : ''
      setValue('devopsTicketNumber', newCurrentTicket)
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      maxWidth="md"
      keepMounted
      transitionDuration={0}
      disablePortal
    >
      <DialogTitle style={{ borderBottom: '1px solid' }} id="alert-dialog-title" sx={{ textAlign: 'center' }}>
        {t('exceptions.editExceptionLog')}
        <IconButton
          aria-label="close"
          onClick={onCancel}
          sx={{
            position: 'absolute',
            right: 15,
            top: 15,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <DialogContent>
          <Grid container direction="column" spacing={2} sx={{ mt: 0, width: 600 }}>
            {updateError && (
              <Grid>
                <Alert severity="error">{t('common.error')}</Alert>
              </Grid>
            )}
            {devopsTicketNumbers && devopsTicketNumbers.length > 0 && (
              <Grid>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <HistoryIcon sx={{ mr: 1 }} />
                  {t('common.ticketHistory')}
                </Typography>
                <List dense>
                  {devopsTicketNumbers.map((ticket, index) => (
                    <ListItem
                      key={`${ticket}-${index}`}
                      component="li"
                      sx={{
                        bgcolor: 'transparent',
                        borderRadius: 1,
                        mb: 0.5,
                        border: '1px solid',
                        borderColor: 'grey.300',
                      }}
                      secondaryAction={
                        <IconButton
                          edge="end"
                          aria-label="delete"
                          onClick={() => handleRemoveTicket(ticket)}
                          size="small"
                        >
                          <DeleteIcon />
                        </IconButton>
                      }
                    >
                      <ListItemText
                        primary={
                          <Link
                            href={DevopsBaseUrl + ticket}
                            target="_blank"
                            rel="noopener noreferrer"
                            underline="hover"
                            color="primary"
                          >
                            {ticket}
                          </Link>
                        }
                        secondary={ticket === getValues('devopsTicketNumber') ? t('common.current') : ''}
                      />
                    </ListItem>
                  ))}
                </List>
              </Grid>
            )}
            <Grid>
              <SelectComponent
                required
                disabled={isUpdating}
                valueList={exceptionStatuses}
                label={t('common.status')}
                {...register('status')}
              />
            </Grid>
            <Grid>
              <ControlledTextField
                name="note"
                control={control}
                multiline
                rows={4}
                label={t('exceptions.notes')}
                disabled={isUpdating}
                placeholder={t('exceptions.addNotePlaceholder')}
                fullWidth
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <AsyncActionButton
            variant="contained"
            type="submit"
            startIcon={<DoneIcon />}
            disabled={isUpdating}
            isPending={isUpdating}
          >
            {t('common.apply')}
          </AsyncActionButton>
        </DialogActions>
      </form>
    </Dialog>
  )
}
export default memo(EditExceptionDialog)
